import { createSlice } from "@reduxjs/toolkit";
import type {
  RaiseComplaintResponsePayload,
  ComplaintStatusResponsePayload,
  ApiEnvelope,
  DispositionItem,
} from "./bbpsComplaintTypes";
import {
  raiseComplaintRequest,
  raiseComplaintSuccess,
  raiseComplaintFailure,
  raiseComplaintReset,
  complaintStatusRequest,
  complaintStatusSuccess,
  complaintStatusFailure,
  complaintStatusReset,
  fetchDispositionsReset,
  fetchDispositionsFailure,
  fetchDispositionsRequest,
  fetchDispositionsSuccess,
} from "./bbpsComplaintActions";

type AsyncBox<T> = {
  loading: boolean;
  data: T | null;
  message: string | null;
  statusCode: number | null;
  error: string | null;
};

export type BbpsComplaintState = {
  raise: AsyncBox<RaiseComplaintResponsePayload>;
  status: AsyncBox<ComplaintStatusResponsePayload>;
  dispositions: AsyncBox<DispositionItem[]>;
};

const emptyAsync = <T>(): AsyncBox<T> => ({
  loading: false,
  data: null,
  message: null,
  statusCode: null,
  error: null,
});

const initialState: BbpsComplaintState = {
  raise: emptyAsync<RaiseComplaintResponsePayload>(),
  status: emptyAsync<ComplaintStatusResponsePayload>(),
  dispositions: emptyAsync<DispositionItem[]>(),
};

const applySuccess = <T>(box: AsyncBox<T>, res: ApiEnvelope<T>) => {
  box.loading = false;
  box.data = res.data ?? null;
  box.message = res.message ?? null;
  box.statusCode = res.statusCode ?? null;
  box.error = null;
};

const applyFailure = <T>(
  box: AsyncBox<T>,
  payload: { message: string; statusCode?: number },
) => {
  box.loading = false;
  box.error = payload.message;
  box.statusCode = payload.statusCode ?? null;
};

const bbpsComplaintSlice = createSlice({
  name: "bbpsComplaint",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Raise complaint
    builder.addCase(raiseComplaintRequest, (state) => {
      state.raise.loading = true;
      state.raise.error = null;
      state.raise.message = null;
      state.raise.statusCode = null;
      state.raise.data = null;
    });
    builder.addCase(raiseComplaintSuccess, (state, action) => {
      applySuccess(state.raise, action.payload);
    });
    builder.addCase(raiseComplaintFailure, (state, action) => {
      applyFailure(state.raise, action.payload);
    });
    builder.addCase(raiseComplaintReset, (state) => {
      state.raise = emptyAsync<RaiseComplaintResponsePayload>();
    });

    // Complaint status
    builder.addCase(complaintStatusRequest, (state) => {
      state.status.loading = true;
      state.status.error = null;
      state.status.message = null;
      state.status.statusCode = null;
      state.status.data = null;
    });
    builder.addCase(complaintStatusSuccess, (state, action) => {
      state.status.loading = false;
      applySuccess(state.status, action.payload);
    });
    builder.addCase(complaintStatusFailure, (state, action) => {
      applyFailure(state.status, action.payload);
    });
    builder.addCase(complaintStatusReset, (state) => {
      state.status = emptyAsync<ComplaintStatusResponsePayload>();
    });

    // Dispositions
    builder.addCase(fetchDispositionsReset, (state) => {
      state.dispositions = {
        loading: false,
        data: null,
        error: null,
        statusCode: null,
        message: null,
      };
    });

    builder.addCase(fetchDispositionsRequest, (state) => {
      state.dispositions.loading = true;
      state.dispositions.error = null;
      state.dispositions.statusCode = null;
      // data ko null mat karo; cache allow
    });

    builder.addCase(fetchDispositionsSuccess, (state, action) => {
      state.dispositions.loading = false;
      state.dispositions.data = action.payload.items;
      state.dispositions.error = null;
      state.dispositions.statusCode = 200;
      state.dispositions.message = action.payload.message ?? null;
    });

    builder.addCase(fetchDispositionsFailure, (state, action) => {
      state.dispositions.loading = false;
      state.dispositions.data = state.dispositions.data ?? null;
      state.dispositions.error = action.payload.message;
      state.dispositions.statusCode = action.payload.statusCode ?? null;
    });
  },
});

export const bbpsComplaintReducer = bbpsComplaintSlice.reducer;
